import { Icon, Button } from '@pwc/pwc-deals-ui';
import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import RichTextEditorInline from 'common/components/RichTextEditor/RichTextEditorInline';
import { getLibraryItemsFromStore } from 'features/contentLibrary/libraryItemService';
import { useSyncedInfo } from 'common/components/Autosync/SyncedInfoProvider.tsx';
import {
  getAnalysisDescription,
  saveDocumentDescriptionThunk,
  descriptionResetToBasisOfPresentation,
} from 'common/engine/reducers/structuredDocumentDescriptionSlice';
import { insertTablesAndCharts } from 'common/components/RichTextEditor/CkEditorExtensions';
import initializeCKEditor from 'common/components/RichTextEditor/initializeCKEditor';
import NonEditableTextEditor from '../AnalysisDescription/NonEditableTextEditor';
import PowerPivotNotSupportedModal from '../../../common/LibraryItemCard/PowerPivot/components/PowerPivotNotSupportedModal';
import {
  ADJUSTMENTS,
  UNABLE_TO_ADD_CONTENT,
} from '../../../common/LibraryItemCard/PowerPivot/constants/PowerPivotErrorMessages';

import './AnalysisRichtextDescription.scss';

const AdjustmentDescriptionEditor = ({
  projectId,
  isReadonly,
  lightModeVariant,
  config,
  onChange,
}) => {
  const dispatch = useDispatch();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;

  const { header, subheader, content, libraryItemId, childrenData } =
    useSelector(getAnalysisDescription);

  const [focused, setFocused] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [initialised, setInitialised] = useState(false);
  const [descriptionEditor, setDescriptionEditor] = useState(null);
  const [richText, setRichText] = useState(
    content.richText || content.plainText
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  const contextStatusInfoItem = useSyncedInfo();

  useEffect(() => {
    if (mounted && descriptionEditor && !isReadonly) {
      insertTablesAndCharts(
        descriptionEditor,
        'rich-text-editor',
        projectId,
        null,
        dispatch,
        contextStatusInfoItem,
        isPwcUser
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, descriptionEditor, mounted, isReadonly, dispatch, isPwcUser]);

  useEffect(() => {
    if (mounted) {
      if (descriptionEditor && childrenData.length > 0 && !initialised) {
        setInitialised(true);
        const { placeholders } = getLibraryItemsFromStore(richText);
        initializeCKEditor({
          projectId,
          editor: descriptionEditor,
          richTextData: richText,
          childrenData: !placeholders.length ? childrenData : placeholders,
          isReadOnly: isReadonly,
          contextStatusInfoItem,
          isPwcUser,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectId,
    childrenData,
    descriptionEditor,
    richText,
    mounted,
    initialised,
    isReadonly,
    isPwcUser,
  ]);

  const onBack = () => {
    dispatch(descriptionResetToBasisOfPresentation());
  };

  const handleOnChange = (value) => {
    setRichText(value);
    if (mounted && richText && libraryItemId) {
      onChange(value);
    }
  };

  const persistRichText = async () => {
    if (mounted && richText) {
      await dispatch(saveDocumentDescriptionThunk(richText, false, projectId));
      setFocused(false);
    }
  };

  const className = `${lightModeVariant ? ' light-mode-variant' : ''}`;

  return (
    <>
      <div>
        <Button
          className="button-menu"
          type="button"
          onClick={onBack}
          data-testid="adjustment-description-back-button"
        >
          <Icon name="left-chevron" width={14} className="fill icon-orange" />
          Back
        </Button>
      </div>
      <div className="description-header-edit" role="heading" aria-level="3">
        <div>{header}</div>
        {!focused && !richText && !isReadonly && (
          <div className="icon-container">
            <Icon name="edit" className="fill icon-grey-alt-lighter" />
          </div>
        )}
      </div>
      {subheader && (
        <div
          className="description-subheader-edit"
          role="heading"
          aria-level="4"
        >
          <div>{subheader}</div>
        </div>
      )}
      {isReadonly && (
        <NonEditableTextEditor
          projectId={projectId}
          richTextData={richText}
          childrenData={childrenData}
        />
      )}
      {!isReadonly && (
        <div
          className={`rich-text-library-item-edit css-override ${className}`}
        >
          <RichTextEditorInline
            richTextData={childrenData.length === 0 ? richText : ''}
            onChange={handleOnChange}
            onInit={(editor) => {
              setDescriptionEditor(editor);
            }}
            onBlur={() => {
              setFocused(false);
              persistRichText();
            }}
            onFocus={() => {
              setFocused(true);
            }}
            isFocused={focused}
            config={{
              ...config,
              placeholder:
                'Add commentary to the adjustment and insert mini-tables and charts',
            }}
          />
        </div>
      )}
      {!isReadonly && mounted && (
        <PowerPivotNotSupportedModal
          title={UNABLE_TO_ADD_CONTENT}
          message={ADJUSTMENTS}
        />
      )}
    </>
  );
};

AdjustmentDescriptionEditor.propTypes = {
  isReadonly: PropTypes.bool,
  lightModeVariant: PropTypes.bool,
};

AdjustmentDescriptionEditor.defaultProps = {
  isReadonly: false,
  lightModeVariant: false,
};
export default AdjustmentDescriptionEditor;
